'use client';

import { useCallback, useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';

import { authStore } from '@/store/auth.store';
import { setOpenSignInModal } from '@/store/modal';
import { openPatientSelection } from '@/store/patient-selection/patient-selection.store';
import { CartOrigin } from '@/types/enums/analytics/cart/cart-analytics.enum';
import { LoginOriginEnum } from '@/types/enums/analytics/login/login-analytics.enum';
import { PatientOption, ScheduleAgeRange } from '@/types/patient-selection.types';
import { getCartOrigin } from '@/utils/cart/get-cart-origin.util';

export interface PatientSelectionParams {
  ageRange?: ScheduleAgeRange;
  onSelectPatient?: (patient: PatientOption) => void;
}

export interface UsePatientSelectionReturn {
  requestPatientSelection: (params: PatientSelectionParams) => void;
}

const loginOriginByCartOrigin: Partial<Record<CartOrigin, LoginOriginEnum>> = {
  [CartOrigin.SEARCH_RESULTS]: LoginOriginEnum.SEARCH_RESULT,
  [CartOrigin.PROFILE]: LoginOriginEnum.ENTITY,
};

function onceAuthResolves(run: () => void): () => void {
  if (authStore.getState().computed.isAuthReady) {
    run();
    return () => {};
  }

  const unsubscribe = authStore.subscribe((state) => {
    if (state.authStatus !== 'loading') {
      unsubscribe();
      run();
    }
  });

  return unsubscribe;
}

export function usePatientSelection(): UsePatientSelectionReturn {
  const pathname = usePathname();
  const cancelPendingRequest = useRef<() => void>(() => {});

  useEffect(() => () => cancelPendingRequest.current(), []);

  const requestPatientSelection = useCallback(({ ageRange, onSelectPatient }: PatientSelectionParams) => {
    cancelPendingRequest.current();

    const openSelection = () => openPatientSelection({ ageRange: ageRange ?? {}, onSelectPatient });

    cancelPendingRequest.current = onceAuthResolves(() => {
      if (!authStore.getState().computed.isLoggedIn) {
        setOpenSignInModal(true, {
          origin: loginOriginByCartOrigin[getCartOrigin(pathname)],
          loginCallback: openSelection,
        });
        return;
      }

      openSelection();
    });
  }, [pathname]);

  return { requestPatientSelection };
}
